import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ChangeEvent,
} from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { GlobalContext } from '../../providers/GlobalProvider';
import CustomModuleHeaderLayout from 'components/Common/Layouts/CustomModuleHeaderLayout';
import { buildCaseQuery } from 'utils/queryHelper';
import MonitorTicketTable from 'components/Monitor/Table/MonitorTicketTable';
import SearchFilterComponent from 'components/Monitor/SearchFilterComponent';
import AuditCardDash from 'components/Monitor/MonitorCardDash';
import { useSnackbar } from 'notistack';
import CasesAPI from 'api/CasesAPI';
import ContentError from 'components/Common/ContentError';
import { Modal } from '@mui/material';
import AddPersonModal from 'components/Monitor/Modals/AddPersonModal';
import ResultsComponent from 'components/Monitor/ResultsTab/Results';
import ClassResultsComponent from 'components/Monitor/ResultsTab/ClassResults';
import ECCNResultsComponent from 'components/Monitor/ResultsTab/ECCNResults';
import ConsolidatedResults from 'components/Monitor/ResultsTab/ConsolidatedResults';
import { EmailAPI } from 'api/EmailAPI';
import { exportDesignTicketReportPDF } from 'components/Common/EntityTicketPdf';
import { readDesignTicket } from 'components/ExportDesign/exportDesignTicketPayload';
import UsersAPI from 'api/UsersAPI';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { saveMonitorListSearch } from 'utils/monitorListSearch';
import {
  getSavedWorkArea,
  getSavedWorkTypeFilter,
  normalizeSavedWorkTypeKeys,
  SAVED_WORK_AREAS,
  SavedWorkAreaKey,
} from 'components/Monitor/savedWorkTaxonomy';

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const Monitor: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  // Read once on mount so a filtered/paginated view survives navigating into a
  // ticket and back (Monitor fully unmounts on that route change, so plain
  // useState alone loses it) — kept in sync with the URL below.
  const initialSearchParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [],
  );
  const parseListParam = (key: string): string[] => {
    const raw = initialSearchParams.get(key);
    return raw ? raw.split(',').filter(Boolean) : [];
  };

  const initialArea = getSavedWorkArea(initialSearchParams.get('area'));
  const [activeAreaKey, setActiveAreaKey] = useState<SavedWorkAreaKey>(
    initialArea.key,
  );
  const [cardTab, setCardTab] = useState<string>(
    initialSearchParams.get('view') === 'List' ? 'List' : 'Card',
  );
  const [pageNum, setPageNum] = useState(() => {
    const parsed = parseInt(initialSearchParams.get('page') || '1', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchVal, setSearchVal] = useState('');
  const [trigger, setTrigger] = useState(false);
  const [caseQuery, setCaseQuery] = useState<any>({});
  const [caseCount, setCaseCount] = useState<number>(0);
  const [areaCounts, setAreaCounts] = useState<
    Record<SavedWorkAreaKey, number | null>
  >({ import: null, export: null, entity: null });
  const [isFetching, setIsFetching] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>({});
  const [triggerSendPdf, setTriggerSendPdf] = useState(false);
  const [sendPdfFileName, setSendPdfFileName] = useState('');
  const [sendPdfEmailRecipients, setSendPdfEmailRecipients] = useState<
    string[]
  >([]);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [cases, setCases] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });
  const [cur_case, setCase] = useState<{
    isLoaded: boolean;
    error: string | undefined;
    data: any;
  }>({ isLoaded: false, error: undefined, data: {} });
  const [selectedOptsType, setSelectedOptsType] = useState<string[]>(() =>
    normalizeSavedWorkTypeKeys(initialArea, parseListParam('types')),
  );
  const [selectedOptsScope, setSelectedOptsScope] = useState<string[]>(() =>
    parseListParam('scope'),
  );
  const [selectedOptsFreq, setSelectedOptsFreq] = useState(() =>
    parseListParam('freq'),
  );
  const [selectedOptsStatus, setSelectedOptsStatus] = useState(() =>
    parseListParam('status'),
  );

  const activeArea = getSavedWorkArea(activeAreaKey);
  const latestRequest = useRef(0);
  const latestCountsRequest = useRef(0);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const buildSavedWorkQuery = (
    query: any,
    area = activeArea,
    selectedTypeKeys = selectedOptsType,
  ) => {
    const includeSharedWith = selectedOptsScope.includes('Shared With Me');
    const favoritesOnly = selectedOptsScope.includes('Favorites');
    const accessQuery = includeSharedWith
      ? [
          { sharedWith: cur_user.basics?.id },
          { sharedWith: cur_user.basics?.email },
        ]
      : [
          { sharedWith: cur_user.basics?.id },
          { sharedWith: cur_user.basics?.email },
          { createdBy: cur_user.basics?.id },
          { 'owner.kc_id': cur_user.basics?.id },
        ];

    return {
      ...query,
      groupId: cur_user.selected_team?.id,
      ...(selectedOptsFreq.length > 0 && {
        frequency: { $in: selectedOptsFreq },
      }),
      ...(selectedOptsStatus.length > 0 && {
        status: { $in: selectedOptsStatus },
      }),
      ...(favoritesOnly && { favorite: true }),
      $and: [
        ...(Array.isArray(query.$and) ? query.$and : []),
        getSavedWorkTypeFilter(area, selectedTypeKeys),
        { $or: accessQuery },
      ],
    };
  };

  const getCasesFromAPI = async (query: any, page: number) => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setIsFetching(true);
    const mongoReq = {
      query: buildSavedWorkQuery(query),
      skip: cardTab === 'List' ? (page - 1) * 10 : (page - 1) * 9,
      limit: cardTab === 'List' ? 10 : 9,
      projection: {
        _id: 1,
        ticketName: 1,
        curNew: 1,
        curTotal: 1,
        status: 1,
        createdAt: 1,
        favorite: 1,
        docType: 1,
        tariffJurisdiction: 1,
        sharedWith: 1,
        comments: 1,
        lastUpdate: 1,
        whitelisted: 1,
        // Just enough of an unreviewed tariff update to badge the row.
        'tariffMonitor.pendingChange.id': 1,
        'tariffMonitor.pendingChange.heading': 1,
        'tariffMonitor.pendingChange.previousValue': 1,
        'tariffMonitor.pendingChange.currentValue': 1,
      },
    };
    try {
      const [casesResponse, countResponse] = await Promise.all([
        CasesAPI.searchCases(mongoReq),
        CasesAPI.countCases(mongoReq),
      ]);

      if (requestId !== latestRequest.current) return;

      if (casesResponse?.success && countResponse?.success) {
        setCases({
          data: casesResponse.result || [],
          isLoaded: true,
          error: undefined,
        });
        setCaseCount(countResponse.result || 0);
      } else {
        const errorMessage = `Error querying cases: ${
          casesResponse?.result || countResponse?.result || 'Unknown error'
        }`;
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setCases({ data: [], isLoaded: true, error: errorMessage });
        setCaseCount(0);
      }
    } catch (error) {
      if (requestId !== latestRequest.current) return;

      const detail = error instanceof Error ? error.message : 'Unknown error';
      const errorMessage = `Error querying cases: ${detail}`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setCases({ data: [], isLoaded: true, error: errorMessage });
      setCaseCount(0);
    } finally {
      if (requestId === latestRequest.current) {
        setIsFetching(false);
      }
    }
  };

  const getAreaCountsFromAPI = async (query: any) => {
    const requestId = latestCountsRequest.current + 1;
    latestCountsRequest.current = requestId;
    try {
      const responses = await Promise.all(
        SAVED_WORK_AREAS.map((area) =>
          CasesAPI.countCases({
            query: buildSavedWorkQuery(query, area, []),
          }),
        ),
      );

      if (requestId !== latestCountsRequest.current) return;

      setAreaCounts((current) => {
        const next = { ...current };
        SAVED_WORK_AREAS.forEach((area, index) => {
          const response = responses[index];
          if (response?.success) next[area.key] = Number(response.result) || 0;
          else next[area.key] = null;
        });
        return next;
      });
    } catch {
      if (requestId !== latestCountsRequest.current) return;
      setAreaCounts({ import: null, export: null, entity: null });
    }
  };

  const handleAdvancedSearch = (search: string) => {
    setCaseQuery(buildCaseQuery(search, ''));
    setPageNum(1);
  };

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    setPageNum(new_page);
  };

  const handleAreaChange = (areaKey: SavedWorkAreaKey) => {
    setActiveAreaKey(areaKey);
    setSelectedOptsType([]);
    setPageNum(1);
  };

  const handleTypeFilterChange = (value: any) => {
    setSelectedOptsType(value);
    setPageNum(1);
  };

  const handleScopeFilterChange = (value: any) => {
    setSelectedOptsScope(value);
    setPageNum(1);
  };

  const handleFrequencyFilterChange = (value: any) => {
    setSelectedOptsFreq(value);
    setPageNum(1);
  };

  const handleStatusFilterChange = (value: any) => {
    setSelectedOptsStatus(value);
    setPageNum(1);
  };

  const handleViewChange: React.Dispatch<React.SetStateAction<string>> = (
    value,
  ) => {
    setCardTab(value);
    setPageNum(1);
  };

  const setVisibleCases = (updatedCases: any[]) => {
    setCases((current) => ({ ...current, data: updatedCases }));
  };

  const addShared = async (emails: any) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `shared with ${emails}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: selectedCase._id,
      sharedWith: [...selectedCase.sharedWith, ...emails],
      activity: [...cur_case.data.activity, actObj],
    };

    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      emails.forEach((email: string) => {
        handleShareTicketNotification(
          cur_case.data.ticketName,
          cur_user,
          email,
          cur_case.data._id,
        );
      });
      enqueueSnackbar('Successfully shared', { variant: 'success' });
    } else {
      const error_msg = 'Error sharing...';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const handleSendCardResultPdf = (payload?: {
    name?: string;
    emails?: string[];
  }) => {
    if (cur_case.data?.docType === 'Design Assistant') {
      sendDesignCardResultPdf(payload);
      return;
    }
    setSendPdfFileName(payload?.name || '');
    setSendPdfEmailRecipients(payload?.emails || []);
    setTriggerSendPdf(true);
  };

  const resetCardPdfSend = () => {
    setTriggerSendPdf(false);
    setSendPdfFileName('');
    setSendPdfEmailRecipients([]);
  };

  const closeShareModal = () => {
    resetCardPdfSend();
    setShareModal(false);
  };

  async function sendDesignCardResultPdf(payload?: { name?: string; emails?: string[] }) {
    const recipients = payload?.emails || [];
    if (recipients.length === 0) {
      enqueueSnackbar('Please add at least one recipient email', { variant: 'error' });
      return;
    }
    if (!cur_case.isLoaded || !cur_case.data?._id) {
      enqueueSnackbar('The saved assessment is still loading. Please try again.', { variant: 'info' });
      return;
    }
    setTriggerSendPdf(true);
    try {
      const ticket = readDesignTicket(cur_case.data);
      const fileName = `${payload?.name || ticket.ticketName || 'Design Assistant'}.pdf`;
      const pdfBlob = await exportDesignTicketReportPDF(ticket, fileName, { shouldDownload: false });
      const senderName = `${cur_user?.basics?.firstName || ''} ${cur_user?.basics?.lastName || ''}`.trim();
      const response = await EmailAPI.sendTariffResultPdf({
        recipients: recipients, fileName: fileName, pdfBase64: await blobToBase64(pdfBlob),
        docType: 'Design Assistant', ticketId: cur_case.data._id, ticketName: ticket.ticketName,
        senderName: senderName, senderEmail: cur_user?.basics?.email,
      });
      if (!response?.success) throw new Error(response?.result || 'Failed to send PDF email');
      enqueueSnackbar('PDF emailed successfully', { variant: 'success' });
      setShareModal(false);
    } catch (error: any) {
      console.error('[Saved Work] Failed to send Design Assistant PDF:', error);
      enqueueSnackbar(`Failed to send result PDF: ${error?.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      resetCardPdfSend();
    }
  }

  const handleCardPdfSendComplete = (success: boolean) => {
    resetCardPdfSend();
    if (success) {
      setShareModal(false);
    }
  };

  const getOwner = async (id: string) => {
    const res = await UsersAPI.getUser(id);
    if (res?.success) {
      return res?.result.basics.email;
    }
    return '';
  };

  useEffect(() => {
    const fetchOwner = async () => {
      if (!selectedCase || !selectedCase._id) {
        return;
      } // Prevent running when empty

      CasesAPI.getCase(selectedCase._id)?.then(async (res: any) => {
        if (res?.success) {
          if (
            res.result.owner?.email !== cur_user.basics?.email &&
            !res.result.sharedWith.includes(cur_user.basics?.email) &&
            !res.result.sharedWith.includes(cur_user.basics?.id) &&
            !(res.result.createdBy === cur_user.basics?.id)
          ) {
            history.push('/');
          }

          setCase({ isLoaded: true, error: undefined, data: res.result });

          if (res.result.owner) {
            setOwnerEmail(res.result.owner?.email);
          } else {
            const email = await getOwner(res.result.createdBy);
            setOwnerEmail(email);
          }
        } else {
          const err_text = `Error loading case: ${res?.result}`;
          enqueueSnackbar(err_text, { variant: 'error' });
        }
      });
    };

    fetchOwner();
  }, [selectedCase]);

  useEffect(() => {
    getCasesFromAPI(caseQuery, pageNum);
  }, [
    trigger,
    pageNum,
    cardTab,
    caseQuery,
    activeAreaKey,
    selectedOptsType,
    selectedOptsScope,
    selectedOptsFreq,
    selectedOptsStatus,
    cur_user.selected_team?.id,
  ]);

  useEffect(() => {
    getAreaCountsFromAPI(caseQuery);
  }, [
    trigger,
    caseQuery,
    selectedOptsScope,
    selectedOptsFreq,
    selectedOptsStatus,
    cur_user.selected_team?.id,
  ]);

  // Keep the current filters/view/page in the URL (replace, not push, so
  // clicking Filters repeatedly doesn't fill up the back-stack) so that
  // navigating into a ticket and back — which fully unmounts this page —
  // restores the same filtered, paginated view instead of resetting it.
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('area', activeAreaKey);
    if (selectedOptsType.length > 0) {
      params.set('types', selectedOptsType.join(','));
    }
    if (selectedOptsScope.length > 0) {
      params.set('scope', selectedOptsScope.join(','));
    }
    if (selectedOptsFreq.length > 0) {
      params.set('freq', selectedOptsFreq.join(','));
    }
    if (selectedOptsStatus.length > 0) {
      params.set('status', selectedOptsStatus.join(','));
    }
    if (cardTab !== 'Card') {
      params.set('view', cardTab);
    }
    if (pageNum !== 1) {
      params.set('page', String(pageNum));
    }
    const search = params.toString();
    const searchWithPrefix = search ? `?${search}` : '';
    history.replace({
      pathname: '/bite-monitor',
      search: searchWithPrefix,
    });
    // The URL alone only helps if the user gets back here via the browser's
    // back button. The ticket page's "Saved Work" breadcrumb does a fresh
    // push instead, so it can't see this — stash it somewhere that survives
    // the trip regardless of how the user navigates back.
    saveMonitorListSearch(searchWithPrefix);
  }, [
    activeAreaKey,
    selectedOptsType,
    selectedOptsScope,
    selectedOptsFreq,
    selectedOptsStatus,
    cardTab,
    pageNum,
  ]);

  const helpContent = (
    <p className='module-header-tooltip-text'>
      Saved Work lets you return to saved items from other modules, print
      outputs, save attachments, and set up rescreening.
    </p>
  );

  if (!cases.isLoaded) {
    return (
      <CustomModuleHeaderLayout title='Saved Work' helpContent={helpContent}>
        <div className='spinner-spacer-table'>
          <div className='loader'>Loading...</div>
        </div>
      </CustomModuleHeaderLayout>
    );
  }
  if (cases.error) {
    return <ContentError error={cases.error} />;
  }

  // Renders the same Results-tab component the ticket detail page uses,
  // off-screen, purely to drive its already-working "email result PDF" flow
  // from the Saved Work card modal without navigating to the ticket page.
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};
  const pdfCaseReady =
    shareModal &&
    cur_case.isLoaded &&
    cur_case.data?._id &&
    cur_case.data._id === selectedCase._id &&
    !!cur_user?.selected_team;
  let hiddenPdfResults: React.ReactNode = null;
  if (pdfCaseReady) {
    const pdfCase = {
      ...cur_case,
      data: {
        ...cur_case.data,
        followUps: Array.isArray(cur_case.data.followUps)
          ? cur_case.data.followUps
          : [],
      },
    };
    const docType = pdfCase.data.docType || '';
    const sharedPdfProps = {
      cur_user: cur_user,
      cur_case: pdfCase,
      trigger: false,
      setTrigger: noop,
      triggerCSV: false,
      setTriggerCSV: noop,
      triggerSendPdf: triggerSendPdf,
      setTriggerSendPdf: setTriggerSendPdf,
      sendPdfFileName: sendPdfFileName,
      sendPdfEmailRecipients: sendPdfEmailRecipients,
      onTariffPdfSendComplete: handleCardPdfSendComplete,
    };

    if (['Classification', 'ECCN'].includes(docType)) {
      hiddenPdfResults = <ClassResultsComponent {...sharedPdfProps} />;
    } else if (['Control Checker', 'ECCN Check'].includes(docType)) {
      hiddenPdfResults = <ECCNResultsComponent {...sharedPdfProps} />;
    } else if (['Import Unified', 'Export Unified'].includes(docType)) {
      hiddenPdfResults = (
        <ConsolidatedResults
          {...sharedPdfProps}
          whitelistEntity={noop}
          setShareModal={noop}
          setIsCommentsModalOpen={noop}
        />
      );
    } else if (docType !== 'Design Assistant') {
      hiddenPdfResults = (
        <ResultsComponent
          {...sharedPdfProps}
          isBulkTicket={docType.toLowerCase().includes('bulk')}
          setDownloadModal={noop}
          setShareModal={noop}
          setIsCommentsModalOpen={noop}
          whitelistEntity={noop}
        />
      );
    }
  }

  return (
    <CustomModuleHeaderLayout title='Saved Work' helpContent={helpContent}>
      <div>
        <div className='saved-work-content'>
          <nav className='saved-work-tabs' aria-label='Saved Work areas'>
            {SAVED_WORK_AREAS.map((area) => {
              const count = areaCounts[area.key];
              return (
                <button
                  key={area.key}
                  type='button'
                  className={`saved-work-tab${
                    activeAreaKey === area.key ? ' active' : ''
                  }`}
                  aria-current={
                    activeAreaKey === area.key ? 'page' : undefined
                  }
                  aria-label={`${area.label}${
                    count === null ? '' : `, ${count} saved items`
                  }`}
                  onClick={() => handleAreaChange(area.key)}
                >
                  {area.label}
                  <span className='saved-work-tab-count' aria-hidden='true'>
                    {count === null ? '–' : count}
                  </span>
                </button>
              );
            })}
          </nav>

          <div className='saved-work-type-filters' aria-label='Saved Work types'>
            <button
              type='button'
              className={`saved-work-type-filter${
                selectedOptsType.length === 0 ? ' active' : ''
              }`}
              onClick={() => handleTypeFilterChange([])}
            >
              All {activeArea.label}
            </button>
            {activeArea.types.map((type) => {
              const isSelected = selectedOptsType.includes(type.key);
              return (
                <button
                  key={type.key}
                  type='button'
                  className={`saved-work-type-filter${
                    isSelected ? ' active' : ''
                  }`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    handleTypeFilterChange((current: string[]) =>
                      current.includes(type.key)
                        ? current.filter((key) => key !== type.key)
                        : current.concat(type.key),
                    )
                  }
                >
                  {type.label}
                </button>
              );
            })}
          </div>

          <SearchFilterComponent
            cardTab={cardTab}
            setCardTab={handleViewChange}
            handleAdvancedSearch={handleAdvancedSearch}
            ticketTypes={activeArea.types}
            selectedOptsType={selectedOptsType}
            setSelectedOptsType={handleTypeFilterChange}
            selectedOptsScope={selectedOptsScope}
            setSelectedOptsScope={handleScopeFilterChange}
            selectedOptsFreq={selectedOptsFreq}
            setSelectedOptsFreq={handleFrequencyFilterChange}
            selectedOptsStatus={selectedOptsStatus}
            setSelectedOptsStatus={handleStatusFilterChange}
          />
          {isFetching && (
            <div className='saved-work-loading' aria-live='polite'>
              <div className='loader'>Loading...</div>
            </div>
          )}
          {!isFetching && cardTab === 'List' && (
            <MonitorTicketTable
              title={`${activeArea.label} Saved Work`}
              handlePageChange={handlePageChange}
              cases={cases.data}
              pageNum={pageNum}
              setCases={setVisibleCases}
              total={caseCount}
              setSearchVal={setSearchVal}
              trigger={trigger}
              setTrigger={setTrigger}
              setSelectedCase={setSelectedCase}
              setShareModal={setShareModal}
            />
          )}
          {!isFetching && cardTab === 'Card' && (
            <AuditCardDash
              handlePageChange={handlePageChange}
              cases={cases.data}
              pageNum={pageNum}
              setCases={setVisibleCases}
              total={caseCount}
              setSearchVal={setSearchVal}
              trigger={trigger}
              setTrigger={setTrigger}
              setSelectedCase={setSelectedCase}
              setShareModal={setShareModal}
            />
          )}
        </div>
        {Object.keys(selectedCase).length > 0 && (
          <Modal open={shareModal} onClose={closeShareModal}>
            <AddPersonModal
              cur_user={cur_user}
              handleClose={closeShareModal}
              sharedArr={selectedCase.sharedWith}
              owner={ownerEmail}
              addShared={addShared}
              handleSend={handleSendCardResultPdf}
              triggerSend={triggerSendPdf}
              isOpen={shareModal}
            />
          </Modal>
        )}
        {hiddenPdfResults && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: '-100000px',
              width: '1200px',
              pointerEvents: 'none',
            }}
          >
            {hiddenPdfResults}
          </div>
        )}
      </div>
    </CustomModuleHeaderLayout>
  );
};

export default Monitor;
